import { useEffect, useRef, useCallback } from "react";

const useAudioPlayer = ({ onEnded }) => {
    const audioRef = useRef(null);
    const currentBlobUrlRef = useRef(null);

    useEffect(() => {
        const audio = new Audio();
        audioRef.current = audio;

        // iOS unlock on first gesture
        const unlock = async () => {
            try {
                await audio.play();
                audio.pause();
            } catch (err) {

                if (err.name !== 'AbortError') {
                    console.error("Error unlocking audio on iOS:", err);
                }
            }
        };

        document.addEventListener("touchstart", unlock, { once: true });
        document.addEventListener("click", unlock, { once: true });

        return () => {
            document.removeEventListener("touchstart", unlock);
            document.removeEventListener("click", unlock);
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = "";
                audioRef.current.load();
            }
            if (currentBlobUrlRef.current) {
                URL.revokeObjectURL(currentBlobUrlRef.current);
                currentBlobUrlRef.current = null;
            }
        };
    }, []);

    const playAudio = useCallback(async (blob) => {
        if (!audioRef.current)
            return;

        const audio = audioRef.current;

        if (currentBlobUrlRef.current) {
            URL.revokeObjectURL(currentBlobUrlRef.current);
            currentBlobUrlRef.current = null;
        }

        audio.pause();
        audio.currentTime = 0;

        const url = URL.createObjectURL(blob);
        audio.src = url;
        audio.load();
        currentBlobUrlRef.current = url; // Store the new URL

        audio.onended = null;

        audio.onended = () => {
            if (url === currentBlobUrlRef.current) {
                URL.revokeObjectURL(url);
                currentBlobUrlRef.current = null;
            }
            if (onEnded)
                onEnded();
        };

        try {
            await audio.play();
        } catch (err) {
            if (err.name === 'AbortError') {
                console.warn("Audio playback aborted by new load request (expected behavior when source changes rapidly).");
            } else {
                console.error("Audio playback failed:", err);
            }
        }
    }, [onEnded]);

    const stopAudio = useCallback(() => {
        if (!audioRef.current)
            return;

        const audio = audioRef.current;

        if (currentBlobUrlRef.current) {
            URL.revokeObjectURL(currentBlobUrlRef.current);
            currentBlobUrlRef.current = null;
        }

        audio.pause();
        audio.currentTime = 0;
    }, []);

    return [playAudio, stopAudio];
};

export default useAudioPlayer;